import { StatusChipColor } from "components/StatusChip";
import {
  CersFacilityMatchStatus,
  CersFacilityMatchSuggestionSource,
  CersFacilityMatchesQuery,
  CersMatchConfidence,
} from "generated-graphql/graphql";

export type Row = CersFacilityMatchesQuery["cersFacilityMatches"]["items"][number];
export type Suggestion = Row["suggestions"][number];
export type RowMode = "matched" | "confirm" | "review" | "find";

/** Confirm inline only for a single HIGH/MEDIUM suggestion; everything else routes to the dialog. */
export function matchRowMode(row: Row): { mode: RowMode; suggestion: Suggestion | null } {
  if (row.matchStatus === CersFacilityMatchStatus.MatchedCers)
    return { mode: "matched", suggestion: null };
  const suggestions = row.suggestions ?? [];
  if (suggestions.length === 0) return { mode: "find", suggestion: null };
  if (suggestions.length > 1) return { mode: "review", suggestion: null };
  const only = suggestions[0];
  const inline =
    only.confidence === CersMatchConfidence.High || only.confidence === CersMatchConfidence.Medium;
  return { mode: inline ? "confirm" : "review", suggestion: only };
}

/**
 * Which fields the CERS row and a candidate share EXACTLY (case-insensitive) —
 * used to bold matching text so corroboration is scannable (team feedback).
 */
export function matchedFields(
  row: Pick<Row, "epaId" | "siteAddress">,
  candidate: {
    streetAddress1?: string | null;
    epaIdStatuses?: { epaId: string; isPrimaryForFacility: boolean; isInactive: boolean }[] | null;
  }
): { epa: boolean; street: boolean } {
  const norm = (v: string | null | undefined) => (v ?? "").trim().toUpperCase();
  const rowEpa = norm(row.epaId);
  const candEpa = norm(bestEpaId(candidate));
  const rowStreet = norm(row.siteAddress);
  const candStreet = norm(candidate.streetAddress1);
  return {
    epa: rowEpa !== "" && rowEpa === candEpa,
    street: rowStreet !== "" && rowStreet === candStreet,
  };
}

export function bestEpaId(candidate: {
  epaIdStatuses?: { epaId: string; isPrimaryForFacility: boolean; isInactive: boolean }[] | null;
}): string | null {
  const statuses = candidate.epaIdStatuses ?? [];
  const primary = statuses.find((s) => s.isPrimaryForFacility && !s.isInactive);
  if (primary) return primary.epaId;
  const active = statuses.find((s) => !s.isInactive);
  if (active) return active.epaId;
  return statuses[0]?.epaId ?? null;
}

/** Why a suggestion earned its confidence — shown under the tier chip in the Match column. */
export function matchBasisLabel(source: CersFacilityMatchSuggestionSource): string {
  switch (source) {
    case CersFacilityMatchSuggestionSource.EpaId:
      return "EPA ID match";
    case CersFacilityMatchSuggestionSource.CersId:
      return "CERS ID match";
    default:
      return "Name & address";
  }
}

export function confidenceDisplay(confidence: CersMatchConfidence): {
  color: StatusChipColor;
  label: string;
} {
  switch (confidence) {
    case CersMatchConfidence.High:
      return { color: "success", label: "High" };
    case CersMatchConfidence.Medium:
      return { color: "warning", label: "Medium" };
    default:
      return { color: "error", label: "Low" };
  }
}
